import {
  Box,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import useApi from "_api/index";
import CustomizeDatePicker from "components/DatePicker";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import { useCarBrands } from "contexts/CarBrandsContext";
import { useFormik } from "formik";
import { useEffect, useMemo } from "react";
import { getBrandName } from "utils/helpers/brandCar";

const ModalContractConsignmentCar = ({
  open,
  onClose,
  car,
  order,
  consignment,
  document,
  rowID,
  type = "consignment_car",
  onSuccess,
  onError,
  disabled = false,
}) => {
  const { carBrands } = useCarBrands();
  const brandName = useMemo(() => {
    if (car && carBrands.length > 0) {
      return getBrandName(car.car_brand_id, carBrands);
    }
    return "ยี่ห้อรถยนต์";
  }, [car, carBrands]);

  const formik = useFormik({
    initialValues: {
      consignment_expire_date: consignment
        ? consignment.consignment_expire_date
        : null,
    },
    validate: (values) => {
      const errors = {};
      if (!values.consignment_expire_date) {
        errors.consignment_expire_date = "กรุณาระบุวันที่สิ้นสุด";
      }
      return errors;
    },
    onSubmit: (values) => {
      console.log("Form submitted with values:", values);
      handleSubmitConsignmentDate(values);
    },
  });

  useEffect(() => {
    console.log("Document changed:", document);
    if (open && document && document.document_id) {
      fetchData();
    }
  }, [open, document]);

  const fetchData = async () => {
    try {
      const response = await useApi.consignment_car.getConsignmentContractById(
        document.document_id
      );
      if (response.success) {
        const data = response.data;
        formik.setValues({
          consignment_expire_date: data.consignment_expire_date || null,
        });
      }
    } catch (error) {
      console.error("Error fetching consignment detail:", error);
    }
  };

  const handleSubmitConsignmentDate = async (values) => {
    try {
      const data = {
        consignment_expire_date: values.consignment_expire_date,
      };
      const response = await useApi.consignment_car.createConsignmentContract(
        data,
        order.order_id
      );
      console.log("Update consignment response:", response);
      if (response.success || response.isStatus) {
        // handlePrintPdfContractConsignment();
        onSuccess && onSuccess("บันทึกข้อมูลสำเร็จ");
      } else {
        onError && onError(response.message);
      }
    } catch (error) {
      console.error("Error updating consignment date:", error);
      onError && onError(error.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          padding: 2,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h4">
          {document && document.document_id
            ? "แก้ไขสัญญารับฝากขาย"
            : "สร้างสัญญารับฝากขาย"}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          padding: 2,
          px: 3,
          mb: 3,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 4,
          },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              mt: 2,
              "& > button": {
                height: "40px",
                cursor: "default",
                pointerEvents: "none",
                background: "rgba(3, 232, 197, 0.2)",
              },
            }}
          >
            <GradientButtonOutline>
              {car ? car.car_type : "รถยนต์"}
            </GradientButtonOutline>
            <GradientButtonOutline>
              {car ? brandName : "ยี่ห้อรถยนต์"}
            </GradientButtonOutline>
            <GradientButtonOutline>
              {car
                ? car.license_plate + " " + car.license_province
                : "ทะเบียนรถ"}
            </GradientButtonOutline>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mt: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              สัญญารับฝากขาย
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="body1">สิ้นสุดสัญญาวันที่</Typography>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <CustomizeDatePicker
                      name="consignment_expire_date"
                      value={formik.values.consignment_expire_date || null}
                      onChange={(date) => {
                        formik.setFieldValue("consignment_expire_date", date);
                      }}
                      error={
                        formik.touched.consignment_expire_date &&
                        Boolean(formik.errors.consignment_expire_date)
                      }
                      helperText={
                        formik.touched.consignment_expire_date &&
                        formik.errors.consignment_expire_date
                      }
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mt: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              ข้อมูลฝากขาย
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1">
                      ยอดเงินที่ต้องการฝากขาย
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={consignment ? consignment.setting_price : ""}
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Typography variant="body1">บาท</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1">ส่วนแบ่งรับฝากขาย</Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={consignment ? consignment.percent_for_sale : ""}
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      เปอร์เซ็น
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </form>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          //   padding: 2,
          borderTop: `1px solid ${colors.grey[300]}`,
        }}
      >
        <GradientButtonOutline onClick={onClose}>กลับ</GradientButtonOutline>
        <GradientButton
          onClick={formik.handleSubmit}
          sx={{
            borderRadius: 2,
          }}
        >
          บันทึก
        </GradientButton>
      </DialogActions>
    </Dialog>
  );
};
export default ModalContractConsignmentCar;
